import React, { useEffect, useRef } from 'react'
import Tooltip from 'components/tooltip'
import Button from 'components/button'
import Input from 'components/input'
import { SIDEBAR_ACTION } from 'components/sidebar/surface'

// The parts a tree row shares whatever it stands for: the guides down its indent and the buttons at
// its right end.

/**
 * Holds a row's buttons at its right end. They show while the row is hovered or has keyboard focus in
 * it, and always where there is no hover (touch screens). Keyboard focus only (focus-visible): a click
 * leaves the row focused, which would keep them showing after the pointer had gone. `invisible` rather
 * than transparent, so a hidden button can't be clicked or tapped.
 */
export const ROW_ACTIONS =
  'absolute right-1 top-1/2 -translate-y-1/2 flex items-center invisible group-hover:visible group-has-[:focus-visible]:visible [@media(hover:none)]:visible'

/**
 * Room on the row's right for its buttons, so a long name doesn't run underneath them: kept only
 * while they show. Whole class names, so Tailwind finds them.
 */
export const ROW_ACTIONS_ROOM: Record<number, string> = {
  0: '',
  1: 'group-hover:pr-8 group-has-[:focus-visible]:pr-8 [@media(hover:none)]:pr-8',
  2: 'group-hover:pr-14 group-has-[:focus-visible]:pr-14 [@media(hover:none)]:pr-14'
}

interface RowActionProps {
  tooltip: string
  label: string
  testId: string
  /** Gets the button, for a menu to open beside it and hand focus back to. */
  onClick: (button: HTMLElement) => void
  /** For a button that opens a menu: whether that menu is open. Left out for any other button. */
  menuOpen?: boolean
  children: React.ReactNode
}

/**
 * A button at a row's right end. Not a drag start, a selection click, a toggle or a link click on
 * the row beneath. No ref of its own: Tooltip takes the button's.
 */
export const RowAction: React.FC<RowActionProps> = ({ tooltip, label, testId, onClick, menuOpen, children }) => (
  <Tooltip content={tooltip}>
    <Button
      data-testid={testId}
      aria-label={label}
      aria-haspopup={menuOpen === undefined ? undefined : 'menu'}
      aria-expanded={menuOpen}
      className={SIDEBAR_ACTION}
      internalType="transparent"
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        // Alt/Option + ↑/↓ would pick up the row it sits in.
        if (event.altKey) event.stopPropagation()
      }}
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onClick(event.currentTarget)
      }}
    >
      {children}
    </Button>
  </Tooltip>
)

/** How a rename ended: the new name, or null for no change; `discarded` when cancelled or left blank. */
export interface RenameResult {
  name: string | null
  discarded: boolean
}

interface RenameInputProps {
  initialName: string
  /** What is being named, for screen readers ("Folder name"). */
  label: string
  testId: string
  /** How the rename ended; `byKey` when Enter or Esc closed it. */
  onDone: (result: RenameResult, byKey: boolean) => void
}

/**
 * The inline rename box. One mount is one rename: Enter or blur saves, Esc cancels, and a blank or
 * unchanged name is no new name. `settled` stops the blur that follows Enter or Esc (the input
 * unmounting) from reporting a second time.
 */
export const RenameInput: React.FC<RenameInputProps> = ({ initialName, label, testId, onDone }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const settled = useRef(false)

  // Imperative focus: the autoFocus prop is disallowed by jsx-a11y/no-autofocus.
  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const finish = (value: string | null, byKey: boolean): void => {
    if (settled.current) return
    settled.current = true
    const name = value?.trim() ?? ''
    onDone({ name: name && name !== initialName ? name : null, discarded: name === '' }, byKey)
  }

  return (
    <Input
      ref={inputRef}
      data-testid={testId}
      aria-label={label}
      // select-text: the row and the sidebar are select-none, which Safari applies to inputs too.
      className="min-w-0 flex-1 h-6 px-1 rounded bg-surface text-body text-[13px] font-medium border border-blue outline-none select-text"
      defaultValue={initialName}
      maxLength={255}
      onKeyDown={(event) => {
        // Keys stay in the box, so nothing listening further up acts on what's being typed.
        event.stopPropagation()
        if (event.key === 'Enter') {
          event.preventDefault()
          finish(event.currentTarget.value, true)
        } else if (event.key === 'Escape') {
          // preventDefault also tells the selection's document-level Escape to leave it alone.
          event.preventDefault()
          finish(null, true)
        }
      }}
      onBlur={(event) => finish(event.currentTarget.value, false)}
    />
  )
}
